import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Mail, MapPin, Phone, Send, CheckCircle2, Calendar, MessageSquare } from "lucide-react";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  company: z.string().trim().max(100).optional(),
  service: z.string().optional(),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

type ContactForm = z.infer<typeof contactSchema>;

const serviceOptions = [
  "CloudOps",
  "DevOps",
  "Kubernetes",
  "Cloud Migration",
  "Custom AI Agents",
  "Cloud Implementation",
  "Something else",
];

const Contact = () => {
  const [form, setForm] = useState<ContactForm>({ name: "", email: "", company: "", service: "", message: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fe: typeof errors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof ContactForm;
        fe[field] = err.message;
      });
      setErrors(fe);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            name: result.data.name,
            email: result.data.email,
            company: result.data.company || null,
            service: result.data.service || null,
            message: result.data.message,
          }),
        }
      );
      setSubmitting(false);
      if (res.ok) setSubmitted(true);
    } catch {
      setSubmitting(false);
    }
  };

  const handleChange = (field: keyof ContactForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const inputCls =
    "w-full h-11 px-4 rounded-lg bg-background/60 border border-border focus:border-primary/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm transition-colors";

  return (
    <Layout>
      <Helmet>
        <title>Contact — Cloudease</title>
        <meta name="description" content="Book a consultation with Cloudease. Cloud, DevOps, Kubernetes, and AI engineering for teams that care about reliability." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-aurora animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">Contact</p>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold tracking-tight mb-6">
              Let's talk <br /><span className="text-gradient">infrastructure.</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Reply within one business day. No decks, no fluff — just a conversation.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left — info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="p-6 rounded-2xl glass">
                <MessageSquare className="h-5 w-5 text-primary mb-4" />
                <h2 className="font-heading font-semibold text-lg mb-2">Reach us</h2>
                <ul className="space-y-3 mt-4">
                  <li className="flex items-center gap-3 text-sm">
                    <div className="h-9 w-9 rounded-lg bg-brand/10 border border-primary/20 flex items-center justify-center">
                      <Mail className="h-4 w-4 text-primary" />
                    </div>
                    <a href="mailto:hello@cloudease.in" className="text-foreground/90 hover:text-foreground">hello@cloudease.in</a>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="h-9 w-9 rounded-lg bg-brand/10 border border-primary/20 flex items-center justify-center">
                      <Phone className="h-4 w-4 text-primary" />
                    </div>
                    <a href="tel:+918778730675" className="text-foreground/90 hover:text-foreground">+91 87787 30675</a>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="h-9 w-9 rounded-lg bg-brand/10 border border-primary/20 flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-foreground/90">Bengaluru, India</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-2xl glass">
                <Calendar className="h-5 w-5 text-primary mb-4" />
                <h2 className="font-heading font-semibold text-lg mb-2">Prefer to book directly?</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Pick a 30-minute slot with an engineer. You'll get a Google Meet invite instantly — sent to you and hello@cloudease.in.
                </p>
                <a
                  href={import.meta.env.VITE_CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="gradient" size="sm" className="rounded-full">
                    <Calendar className="h-4 w-4" /> Book a slot
                  </Button>
                </a>
              </div>

              <div className="p-6 rounded-2xl bg-brand text-white">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/70 mb-3">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse-glow" /> Response time
                </div>
                <div className="text-4xl font-heading font-semibold">&lt; 24h</div>
                <p className="text-sm text-white/80 mt-2">Every inquiry, every business day.</p>
              </div>
            </div>

            {/* Right — form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-12 rounded-2xl glass text-center"
                >
                  <div className="h-16 w-16 rounded-full bg-brand/10 border border-primary/30 mx-auto mb-4 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-heading font-semibold mb-2">Message received.</h3>
                  <p className="text-muted-foreground mb-6">We'll reply within one business day.</p>
                  <Button variant="outline" className="rounded-full" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", company: "", service: "", message: "" }); }}>
                    Send another
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="p-8 md:p-10 rounded-2xl glass space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Name</label>
                      <input id="name" value={form.name} onChange={(e) => handleChange("name", e.target.value)} className={inputCls} placeholder="Your name" />
                      {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Email</label>
                      <input id="email" type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className={inputCls} placeholder="you@company.com" />
                      {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="company" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Company</label>
                      <input id="company" value={form.company} onChange={(e) => handleChange("company", e.target.value)} className={inputCls} placeholder="Company name" />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Service</label>
                      <select id="service" value={form.service} onChange={(e) => handleChange("service", e.target.value)} className={inputCls}>
                        <option value="">Select a service</option>
                        {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Message</label>
                    <textarea id="message" rows={5} value={form.message} onChange={(e) => handleChange("message", e.target.value)} className={inputCls + " h-auto py-3 resize-none"} placeholder="Tell us about your infrastructure and where it hurts..." />
                    {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
                  </div>
                  <Button type="submit" variant="gradient" size="lg" className="w-full rounded-full" disabled={submitting}>
                    {submitting ? "Sending..." : "Send message"} <Send className="h-4 w-4" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
